// Central registry for product-page decoration add-ons. Each entry defines only
// what differs: its UI selectors, selected variant resolver, requirements, and
// cart properties. Selection visibility, validation, cart linking, and adding
// the add-on line item are shared by every decoration type.

type CartProperties = Record<string, string | boolean | null>;

interface DecorationAddon {
  decorationType: string;
  presenceSelector: string;
  selectionContainer: string;
  addonFlag: string;
  groupProperty: string;
  groupPrefix: string;
  requiredFieldPrefix: string;
  requiredFields: string[];
  getVariantId: () => any;
  getProperties: () => CartProperties;
  getParentProperties: () => CartProperties;
}

const selectedValue = (inputName: string): string | null => {
  return ($('input[name="' + inputName + '"]:checked').val() as string) || null;
};

const selectedVariantId = (inputName: string) => {
  return $('input[name="' + inputName + '"]:checked').data('variant-id');
};

const getTotalSelectedGarmentQuantity = (): number => {
  const variantSize = parseInt(($('#variant-size').val() as string) || '0', 10);
  let totalQuantity = 0;

  for (let i = 0; i < variantSize; i++) {
    const quantity = parseInt($('#quantity-' + i).val() as string, 10);

    if (!isNaN(quantity) && quantity > 0) {
      totalQuantity += quantity;
    }
  }

  return totalQuantity;
};

const getFauxLeatherPatchVariantId = () => {
  const size = selectedValue('faux-leather-patch-size');
  const color = selectedValue('faux-leather-patch-color');

  if (!size || !color) {
    return null;
  }

  const matchingVariant = $('.faux-leather-patch-variant').filter(function () {
    return (
      String($(this).data('color')) === String(color) &&
      String($(this).data('size')) === String(size)
    );
  }).first();

  return matchingVariant.length ? matchingVariant.data('variant-id') : null;
};

// Filters the available options for a given field (size or color) based on the selected value of the other field
const filterFauxLeatherOptions = (field: string, byField: string): void => {
  const counterpart = selectedValue('faux-leather-patch-' + byField);
  const $variants = $('.faux-leather-patch-variant');

  // Toggle the visibility of each option based on whether a matching variant exists for the selected counterpart value
  $('input[name="faux-leather-patch-' + field + '"]').each(function () {
    const $input = $(this); 
    const value = String($input.val()); 

    // Check if there is a matching variant for the selected counterpart value and the current option value
    const isAvailable =
      !counterpart ||
      $variants.is(function () {
        return (
          String($(this).data(byField)) === String(counterpart) &&
          String($(this).data(field)) === value
        );
      });  
 
    $input.closest('.deco-pill, .deco-chip').toggle(isAvailable);

    if (!isAvailable) {
      $input.prop('checked', false);
    }
  }); 
};   

export const DECORATION_ADDONS: DecorationAddon[] = [
  {
    decorationType: 'Faux Leather Patch',
    presenceSelector: '.faux-leather-patch-variant',
    selectionContainer: '.faux-leather-patch-selection',
    addonFlag: '_faux_leather_patch',
    groupProperty: '_faux_leather_patch_group',
    groupPrefix: 'flp',
    requiredFieldPrefix: 'faux-leather-patch',
    requiredFields: ['size', 'shape', 'color'],
    getVariantId: getFauxLeatherPatchVariantId,
    getProperties: () => ({ Shape: selectedValue('faux-leather-patch-shape') }),
    getParentProperties: () => ({
      '_Patch size': selectedValue('faux-leather-patch-size'),
      '_Patch shape': selectedValue('faux-leather-patch-shape'),
      '_Patch color': $('input[name="faux-leather-patch-color"]:checked').data('color-label') || null
    })
  },
  {
    decorationType: 'Embroidered Patch',
    presenceSelector: 'input[name="embroidered-patch-size"]',
    selectionContainer: '.embroidered-patch-selection',
    addonFlag: '_embroidered_patch',
    groupProperty: '_embroidered_patch_group',
    groupPrefix: 'ep',
    requiredFieldPrefix: 'embroidered-patch',
    requiredFields: ['size', 'shape'],
    getVariantId: () => selectedVariantId('embroidered-patch-size'),
    getProperties: () => ({ Shape: selectedValue('embroidered-patch-shape') }),
    getParentProperties: () => ({
      '_Patch size': selectedValue('embroidered-patch-size'),
      '_Patch shape': selectedValue('embroidered-patch-shape')
    })
  },
  {
    decorationType: 'PV+ Patch',
    presenceSelector: 'input[name="pv-plus-patch-size"]',
    selectionContainer: '.pv-plus-patch-selection',
    addonFlag: '_pv_plus_patch',
    groupProperty: '_pv_plus_patch_group',
    groupPrefix: 'pvp',
    requiredFieldPrefix: 'pv-plus-patch',
    requiredFields: ['size', 'shape'],
    getVariantId: () => selectedVariantId('pv-plus-patch-size'),
    getProperties: () => ({ Shape: selectedValue('pv-plus-patch-shape') }),
    getParentProperties: () => ({
      '_Patch size': selectedValue('pv-plus-patch-size'),
      '_Patch shape': selectedValue('pv-plus-patch-shape')
    })
  },
  {
    decorationType: 'Flexbroidered Patch',
    presenceSelector: 'input[name="flexbroidered-patch-size"]',
    selectionContainer: '.flexbroidered-patch-selection',
    addonFlag: '_flexbroidered_patch',
    groupProperty: '_flexbroidered_patch_group',
    groupPrefix: 'fbp',
    requiredFieldPrefix: 'flexbroidered-patch',
    requiredFields: ['size', 'shape'],
    getVariantId: () => selectedVariantId('flexbroidered-patch-size'),
    getProperties: () => ({ Shape: selectedValue('flexbroidered-patch-shape') }),
    getParentProperties: () => ({
      '_Patch size': selectedValue('flexbroidered-patch-size'),
      '_Patch shape': selectedValue('flexbroidered-patch-shape')
    })
  },
  {
    decorationType: 'Sublimated Patch',
    presenceSelector: 'input[name="sublimated-patch-size"]',
    selectionContainer: '.sublimated-patch-selection',
    addonFlag: '_sublimated_patch',
    groupProperty: '_sublimated_patch_group',
    groupPrefix: 'sp',
    requiredFieldPrefix: 'sublimated-patch',
    requiredFields: ['size', 'shape'],
    getVariantId: () => selectedVariantId('sublimated-patch-size'),
    getProperties: () => ({ Shape: selectedValue('sublimated-patch-shape') }),
    getParentProperties: () => ({
      '_Patch size': selectedValue('sublimated-patch-size'),
      '_Patch shape': selectedValue('sublimated-patch-shape')
    })
  },
  {
    decorationType: 'Puff Embroidered',
    presenceSelector: 'input[name="puff-embroidered-size"]',
    selectionContainer: '.puff-embroidered-selection',
    addonFlag: '_puff_embroidered',
    groupProperty: '_puff_embroidered_group',
    groupPrefix: 'pe',
    requiredFieldPrefix: 'puff-embroidered',
    requiredFields: ['size'],
    getVariantId: () => selectedVariantId('puff-embroidered-size'),
    getProperties: () => ({}),
    getParentProperties: () => ({ '_Patch size': selectedValue('puff-embroidered-size') })
  },
  {
    decorationType: 'Puff Print',
    presenceSelector: 'input[name="puff-print-color"]',
    selectionContainer: '.puff-print-selection',
    addonFlag: '_puff_print',
    groupProperty: '_puff_print_group',
    groupPrefix: 'pp',
    requiredFieldPrefix: 'puff-print',
    requiredFields: ['color'],
    getVariantId: () => $('.puff-print-variant').first().data('variant-id'),
    getProperties: () => ({ Color: selectedValue('puff-print-color') }),
    getParentProperties: () => ({
      '_Patch size': $('.puff-print-variant').first().data('title') || null,
      '_Patch color': selectedValue('puff-print-color')
    })
  }
];

const getSelectedDecorationAddon = (): DecorationAddon | undefined => {
  const decorationType = $('input[name="properties[_Decoration type]"]:checked').val();
  return DECORATION_ADDONS.find(addon => addon.decorationType === decorationType);
};

const toggleDecorationSelections = (): void => {
  const selectedAddon = getSelectedDecorationAddon();

  DECORATION_ADDONS.forEach(addon => {
    $(addon.selectionContainer).toggle(selectedAddon === addon);
  });
};

export const initDecorationAddons = (): void => {
  if (!DECORATION_ADDONS.some(addon => $(addon.presenceSelector).length > 0)) {
    return;
  }

  $('input[name="properties[_Decoration type]"]').on('change', toggleDecorationSelections);
  toggleDecorationSelections();

  $('input[name="faux-leather-patch-color"]').on('change', () => filterFauxLeatherOptions('size', 'color'));
  $('input[name="faux-leather-patch-size"]').on('change', () => filterFauxLeatherOptions('color', 'size'));
  filterFauxLeatherOptions('size', 'color');
  filterFauxLeatherOptions('color', 'size');
};

// These are the property names that will be attached to the garment items in the cart
const PARENT_DECORATION_PROPERTY_NAMES = ['_Patch size', '_Patch shape', '_Patch color'];

// Returns an error message when the selected decoration has an incomplete selection; otherwise null
const getDecorationValidationError = (addon: DecorationAddon): string | null => {
  const missingField = addon.requiredFields.find(field => !selectedValue(addon.requiredFieldPrefix + '-' + field));

  return missingField ? 'Please select a ' + missingField + ' for the ' + addon.decorationType + ' option.' : null;
};

// Attaches the selected decoration's group token and option values to the garment items,
// or returns a validation error message when the selection is incomplete.
export const prepareDecorationAddonCartItem = (): string | null => {
  const $form = $('#bold-form-options');

  // Clear any group tokens and parent decoration attributes from a prior selection.
  DECORATION_ADDONS.forEach(addon => {
    $form.find('input[name="properties[' + addon.groupProperty + ']"]').remove();
  });
  PARENT_DECORATION_PROPERTY_NAMES.forEach(name => {
    $form.find('input[name="properties[' + name + ']"]').remove();
  });

  const selectedAddon = getSelectedDecorationAddon();

  if (!selectedAddon) {
    return null;
  }

  const validationError = getDecorationValidationError(selectedAddon);

  if (validationError) {
    return validationError;
  }

  const groupToken = selectedAddon.groupPrefix + '-' + Date.now() + '-' + Math.floor(Math.random() * 1e9);

  $('<input>', {
    type: 'hidden',
    name: 'properties[' + selectedAddon.groupProperty + ']',
    value: groupToken
  }).appendTo($form);

  const parentProperties = selectedAddon.getParentProperties();
  Object.keys(parentProperties).forEach(name => {
    const value = parentProperties[name];
    if (value) {
      $('<input>', {
        type: 'hidden',
        name: 'properties[' + name + ']',
        value: value as string
      }).appendTo($form);
    }
  });

  return null;
};

export const addSelectedDecorationAddonToCart = async (): Promise<any> => {
  const selectedAddon = getSelectedDecorationAddon();

  if (!selectedAddon) {
    return;
  }

  const variantId = selectedAddon.getVariantId();
  const groupToken = $('#bold-form-options input[name="properties[' + selectedAddon.groupProperty + ']"]').val();

  if (!variantId || !groupToken) {
    return;
  }

  const quantity = getTotalSelectedGarmentQuantity();

  if (quantity <= 0) {
    return;
  }

  return axios.post(
    '/cart/add.js',
    {
      items: [
        {
          id: variantId,
          quantity: quantity,
          properties: {
            [selectedAddon.addonFlag]: true,
            [selectedAddon.groupProperty]: groupToken,
            ...selectedAddon.getProperties()
          }
        }
      ]
    },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
};
